import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ensureSessionForReplay } from '../auth/ensureSessionForReplay';
import { DEFAULT_GAME_SOURCES } from '../common/gameSource';
import GameSummaryTable from '../games/shared/GameSummaryTable';
import {
  fetchReplayHistory,
  fetchTopUnviewedReplayGames,
  REPLAY_DEFAULT_MAX_ELO,
  REPLAY_DEFAULT_MIN_ELO,
  type ReplayGameSummary,
} from '../replay/replayApi';
import { recordReplayViewForSelect } from '../replay/replayLimitToast';
import { buildReplayLocationState } from '../replay/replayLocationState';
import { replayGamePath, replayHubPath } from '../replay/replayUrl';
import DashboardSection from './DashboardSection';
import { dashboardWrappedGameTableSx } from './dashboardStyles';

const REPLAY_SLOT_LIMIT = 8;
/** Reserve at least half the row for unviewed games when the user has enough history. */
const MAX_VIEWED_ON_DASHBOARD = REPLAY_SLOT_LIMIT / 2;

const buildDashboardReplays = (
  viewed: ReplayGameSummary[],
  unviewed: ReplayGameSummary[],
): ReplayGameSummary[] => {
  const viewedSlice = viewed.slice(0, MAX_VIEWED_ON_DASHBOARD);
  const viewedIds = new Set(viewedSlice.map((game) => game.gameId));
  const remaining = REPLAY_SLOT_LIMIT - viewedSlice.length;

  const unviewedFill =
    remaining > 0
      ? unviewed
          .filter((game) => !viewedIds.has(game.gameId))
          .slice(0, remaining)
      : [];

  return [...viewedSlice, ...unviewedFill];
};

export const RecentReplaySection = () => {
  const navigate = useNavigate();
  const { isGuest, isLoggedIn, continueAsGuest } = useAuth();
  const [games, setGames] = useState<ReplayGameSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage('');

      const [historyResult, unviewedResult] = await Promise.all([
        fetchReplayHistory({ limit: MAX_VIEWED_ON_DASHBOARD }).then(
          (value) => ({ status: 'fulfilled' as const, value }),
          (reason) => ({ status: 'rejected' as const, reason }),
        ),
        fetchTopUnviewedReplayGames({
          limit: REPLAY_SLOT_LIMIT,
          minElo: REPLAY_DEFAULT_MIN_ELO,
          maxElo: REPLAY_DEFAULT_MAX_ELO,
          sources: [...DEFAULT_GAME_SOURCES],
          sort: 'top',
        }).then(
          (value) => ({ status: 'fulfilled' as const, value }),
          (reason) => ({ status: 'rejected' as const, reason }),
        ),
      ]);

      if (cancelled) {
        return;
      }

      const viewedGames =
        historyResult.status === 'fulfilled' ? historyResult.value.games : [];
      const unviewedGames =
        unviewedResult.status === 'fulfilled' ? unviewedResult.value.games : [];

      setGames(buildDashboardReplays(viewedGames, unviewedGames));

      if (historyResult.status === 'rejected' && unviewedResult.status === 'rejected') {
        const reason =
          historyResult.reason instanceof Error
            ? historyResult.reason.message
            : 'Failed to load replays';
        setErrorMessage(reason);
      }

      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGameSelect = async (game: ReplayGameSummary) => {
    const guest = await ensureSessionForReplay(
      isLoggedIn,
      isGuest,
      continueAsGuest,
    );
    const allowed = await recordReplayViewForSelect(game.gameId, guest);
    if (!allowed) {
      return;
    }

    navigate(replayGamePath(game.gameId), {
      state: buildReplayLocationState(
        {
          white: game.white,
          black: game.black,
          whiteElo: game.whiteElo,
          blackElo: game.blackElo,
          eco: game.eco,
          opening: game.opening,
          openingSans: game.openingSans,
          timeControl: game.timeControl,
          timeClass: game.timeClass,
          date: game.date,
          result: game.result,
        },
        { returnTo: '/dashboard' },
      ),
    });
  };

  return (
    <DashboardSection
      title="Replays"
      action={
        <Button component={RouterLink} to={replayHubPath()} size="small">
          Replay hub
        </Button>
      }
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && errorMessage && (
        <Alert severity="warning">{errorMessage}</Alert>
      )}

      {!loading && !errorMessage && games.length === 0 ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No replays available yet.
          </Typography>
          <Button component={RouterLink} to={replayHubPath()} size="small">
            Start replaying
          </Button>
        </Box>
      ) : (
        !loading &&
        !errorMessage && (
          <GameSummaryTable
            games={games}
            emptyMessage="No replays available yet."
            onGameSelect={(game) => void handleGameSelect(game)}
            wrappedRows
            tableSx={dashboardWrappedGameTableSx}
          />
        )
      )}
    </DashboardSection>
  );
};

export default RecentReplaySection;
